// =============================================================================
// Toasts — non-blocking transient feedback.
// =============================================================================
// Replaces the blocking window.alert() calls that failure paths used to make.
// An alert() freezes the whole app, can't be styled, and on a failed message
// send it stole focus from the composer the user was about to retry in.
//
// Deliberately its own tiny store rather than a slice of store.ts: toasts are
// ephemeral (never persisted) and every component that fires one would
// otherwise re-render on unrelated store churn. `toast.*` is callable from
// non-React code (api catch blocks, event handlers) because it talks to the
// store directly rather than through a hook.
//
// Rendered once, near the root — see ToastHost in App.tsx.
// =============================================================================

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { Icon } from './ui';

export type ToastKind = 'error' | 'success' | 'info';

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastStore {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
}

// Errors linger longer than confirmations — you may need to read and act on them.
const DURATION: Record<ToastKind, number> = {
  error: 6000,
  success: 3000,
  info: 4000,
};

const MAX_VISIBLE = 4;

let nextId = 1;

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (kind, message) => {
    const id = nextId++;
    set((s) => ({
      // Cap the stack so a retry loop can't paper over the whole screen.
      toasts: [...s.toasts, { id, kind, message }].slice(-MAX_VISIBLE),
    }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, DURATION[kind]);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/**
 * Fire a toast from anywhere — React or not.
 *
 * `toast.error(err, 'Failed to send')` accepts an unknown error directly and
 * pulls a message off it, so call sites don't each re-implement the
 * `err instanceof ApiError ? err.message : 'unknown error'` dance.
 */
export const toast = {
  error(err: unknown, fallback = 'Something went wrong') {
    const detail =
      err instanceof Error && err.message ? err.message : typeof err === 'string' ? err : '';
    useToastStore.getState().push('error', detail ? `${fallback}: ${detail}` : fallback);
  },
  /** Plain error text, when there's no Error object to unwrap. */
  errorText(message: string) {
    useToastStore.getState().push('error', message);
  },
  success(message: string) {
    useToastStore.getState().push('success', message);
  },
  info(message: string) {
    useToastStore.getState().push('info', message);
  },
};

const KIND_STYLE: Record<ToastKind, { icon: 'x' | 'check' | 'bell'; accent: string }> = {
  // Fixed status colors, not theme tokens — a failure must read as a failure
  // on all 17 themes (see .ai-style-rules.md).
  error: { icon: 'x', accent: 'bg-danger' },
  success: { icon: 'check', accent: 'bg-[#3F9142]' },
  info: { icon: 'bell', accent: 'bg-accent' },
};

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // Esc clears the stack — the same reflex the alert() it replaced trained.
  useEffect(() => {
    if (toasts.length === 0) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') useToastStore.setState({ toasts: [] });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toasts.length]);

  if (toasts.length === 0) return null;

  return createPortal(
    <div
      // Bottom-anchored and fixed, so it carries the iOS safe-area inset itself
      // (documented pattern in CLAUDE.md — the root deliberately has no bottom
      // inset). pointer-events-none on the stack so it never eats clicks meant
      // for the composer underneath; re-enabled per toast.
      className="fixed left-1/2 -translate-x-1/2 z-100 flex flex-col gap-2 w-[min(26rem,calc(100vw-2rem))] pointer-events-none"
      style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const style = KIND_STYLE[t.kind];
        return (
          <div
            key={t.id}
            className="hybrid-card pointer-events-auto flex items-start gap-2 p-2.5 pr-1.5 animate-slide-up shadow-lg"
          >
            <span
              className={`${style.accent} text-white rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-px`}
            >
              <Icon name={style.icon} size={12} />
            </span>
            <span className="text-xs text-text leading-snug flex-1 min-w-0 wrap-break-word">
              {t.message}
            </span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="nav-icon-btn shrink-0"
              title="Dismiss"
              aria-label="Dismiss notification"
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
}
